/**
 * EntityAvatar – Profile/entity image with initials fallback (no placeholders).
 * Use for drivers, users, beneficiaries: shows photo when available, else initials.
 * Location: packages/ui/src/components/EntityAvatar.tsx
 */

import * as React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { cn } from '../lib/utils';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface EntityAvatarProps {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-base',
};

export const EntityAvatar = React.forwardRef<HTMLDivElement, EntityAvatarProps>(
  ({ name, src, size = 'md', className }, ref) => {
    const initials = getInitials(name);
    return (
      <Avatar
        ref={ref}
        className={cn(sizeClasses[size], className)}
      >
        {src ? (
          <AvatarImage src={src} alt={name} className="object-cover" />
        ) : null}
        <AvatarFallback className="bg-primary/10 text-primary font-medium">
          {initials}
        </AvatarFallback>
      </Avatar>
    );
  }
);
EntityAvatar.displayName = 'EntityAvatar';

/**
 * EntityIconAvatar – Icon-based avatar for non-person entities (ATM, vehicle, POS).
 * No placeholder: always shows a clear icon.
 */
export interface EntityIconAvatarProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  status?: 'active' | 'inactive' | 'warning' | 'error';
}

const iconSizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const statusBgClasses = {
  active: 'bg-success/10 text-success',
  inactive: 'bg-muted text-muted-foreground',
  warning: 'bg-warning/10 text-warning',
  error: 'bg-destructive/10 text-destructive',
};

export function EntityIconAvatar({
  icon: Icon,
  label,
  size = 'md',
  className,
  status = 'active',
}: EntityIconAvatarProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        iconSizeClasses[size],
        statusBgClasses[status],
        className
      )}
      title={label}
      aria-hidden
    >
      <Icon className={cn(size === 'sm' && 'h-4 w-4', size === 'md' && 'h-5 w-5', size === 'lg' && 'h-6 w-6')} />
    </div>
  );
}
